import {useCallback, useEffect, useRef, useState} from 'react';
import {BackendApi} from '../api/api.ts';
import {BackendAPITypes} from '../generated/blueprint.ts';

export type MatchPartsResponse =
    BackendAPITypes.BACKENDPreHandleSyncApiV1ReconciliationGetMatchDetailsGetResponseBody;

export type MatchTransactionPartResponse = MatchPartsResponse['transaction_parts'][number];
export type MatchBillPartResponse = MatchPartsResponse['bill_parts'][number];

type Selector =
    | {by: 'matchId'; id: string | null}
    | {by: 'documentId'; id: string | null};

interface UseMatchDetailsResult {
    data: MatchPartsResponse | null;
    isLoading: boolean;
    error: string | null;
    reload: () => Promise<void>;
}

function useMatchDetailsInternal(api: BackendApi, selector: Selector): UseMatchDetailsResult {
    const [data, setData] = useState<MatchPartsResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const {by, id} = selector;

    const apiRef = useRef(api);
    apiRef.current = api;

    const reload = useCallback(async () => {
        if (!id) {
            setData(null);
            setError(null);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const response = by === 'matchId'
                ? await apiRef.current.Reconciliation__getMatchDetails(id)
                : await apiRef.current.Reconciliation__getMatchDetailsByDocumentId(id);
            setData(response);
        } catch (e) {
            setData(null);
            setError('Er is een fout opgetreden bij het laden van de match.');
        } finally {
            setIsLoading(false);
        }
    }, [by, id]);

    useEffect(() => {
        reload();
    }, [reload]);

    return {data, isLoading, error, reload};
}

export function useMatchDetailsByMatchId(api: BackendApi, matchId: string | null): UseMatchDetailsResult {
    return useMatchDetailsInternal(api, {by: 'matchId', id: matchId});
}

export function useMatchDetailsByDocumentId(api: BackendApi, documentId: string | null, enabled: boolean = true): UseMatchDetailsResult {
    return useMatchDetailsInternal(api, {by: 'documentId', id: enabled ? documentId : null});
}
